<template>
  <div class="custom-spus">
    <div class="card">
      <div v-if="props.isCustomSpu"
           class="title">
        <left-square-outlined @click="onGoBack" />
        <h4>{{ $t('CustomSpus.CustomSpu.viewProduct') }}</h4>
      </div>
      <div class="content">
        <div class="aside">
          <div class="top">
            <div ref="bigContainer"
                 class="big">
              <ImageCustomizeViewer v-if="selectedCustomizedImage &&
                bigContainer &&
                bigContainer.offsetHeight &&
                bigContainer.offsetWidth
              "
                                    :id="selectedCustomizedImage.id + ''"
                                    :key="selectedCustomizedImage.id + ''"
                                    mode="view"
                                    :container="bigContainer"
                                    :template-basic-parameters="templateBasicParameters(customImage, currentCustomSpuData.selectedColor?.value!, selectedCustomizedImage.width, selectedCustomizedImage.height)"
                                    :custom-area="assignOriginalCustomizedData(selectedCustomizedImage)"
                                    :customized-image="toUrl(selectedCustomizedImage.customized_image)
                                      " />
            </div>
          </div>
          <div class="thumbnail">
            <div ref="thumbnailContainer"
                 class="hidden"></div>
            <div v-for="(item, index) in thumbnails"
                 :key="`thumbnail-container-${item.id}-${index}`"
                 class="thumbnail-container"
                 @click="onSelect(item)">
              <ImageCustomizeViewer v-if="thumbnailWrapper &&
                thumbnailWrapper.width &&
                thumbnailWrapper.height
              "
                                    :id="`Product-${item.id}-${index}`"
                                    :ref="createEditorRef"
                                    :name="item.name"
                                    mode="view"
                                    :color-name="item.color?.name"
                                    :container="thumbnailContainer"
                                    :template-basic-parameters="templateBasicParameters(toUrl(findCustomableImage(item.color!, item))!, item.color?.value!, item.width, item.height)"
                                    :custom-area="assignOriginalCustomizedData(item)"
                                    :customized-image="toUrl(item.customized_image)" />
            </div>
          </div>
          <div class="operation">
            <a-button :type="currentBtn === 'download' ? 'primary' : ''"
                      block
                      @click="download">
              <vertical-align-bottom-outlined />{{ $t('CustomSpus.CustomSpu.batchDownload') }}
            </a-button>
          </div>
        </div>
        <div class="detail">
          <CustomSpuDetail :custom-spu="customSpu"
                           :colors="colors"
                           :sizes="sizes"
                           :custom-skus="customSkus" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toUrl } from "@/utils"
import { ImageCustomizeEditor as ImageCustomizeViewer, ImageCustomizeEditor } from "@iota-cn/ui-components"
// import ImageCustomizeEditor from "./ImageCustomizeEditor.vue"
import { saveAs } from "file-saver"
import JSZip from "jszip"
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { fetchCustomSpu } from "../api"
import type {
  Color,
  CustomSpu,
  CustomizedImage,
  SimpleSku,
  CustomPriceDetail,
  CustomPrice
} from "../types"
import {
  convertColor,
  convertCustomSpuToSimpleSpu,
  convertToCustomizedImage,
  findCustomableImage,
  templateBasicParameters,
  assignOriginalCustomizedData
} from "../utils"
import CustomSpuDetail from "./components/CustomSpuDetail.vue"
import BigNumber from "bignumber.js"
import { selectedSpuData } from "../../index"
import { message } from "ant-design-vue"
import { useI18n } from 'vue-i18n'

BigNumber.config({ ROUNDING_MODE: 1 })

const props = defineProps<{
  spuData: any
  skuData: any
  isCustomSpu: boolean
}>()
const currentCustomSpuData = selectedSpuData()
const currentBtn = ref<string>("")
const colors = ref<Color[]>([])
const { t } = useI18n()
const sizes = ref<string[]>([])
const customImage = ref<string>("")
const customSkus = ref<SimpleSku[]>([])
type FabricElement = InstanceType<typeof ImageCustomizeEditor>
// 小图循环数据
const thumbnails = ref<CustomizedImage[]>([])
const customSpu = ref<CustomSpu>()
const route = useRoute()

watch(
  () => currentCustomSpuData.selectedColor,
  () => {
    if (selectedCustomizedImage.value && currentCustomSpuData.selectedColor)
      customImage.value = toUrl(
        findCustomableImage(
          currentCustomSpuData.selectedColor,
          selectedCustomizedImage.value
        )
      )!
  },
  {
    deep: true
  }
)

onMounted(async () => {
  const id = route.params.id
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await fetchCustomSpu(id as string)
  const data = {} as CustomPriceDetail
  data.spu_price = res.spu.price
  const custom_price = [] as Array<CustomPrice>
  res.customizedImages?.forEach((image: any) => {
    if (image.customableImage) {
      let customized_areas_scale_title = ''
      image.customableImage?.scales?.forEach((scale: any) => {
        if (scale.customized_areas_scale == image.customized_areas.customized_areas_scale) {
          customized_areas_scale_title = scale.title
        }
      })
      let dss_image_price = '0'
      if (res.references && res.references.length > 0) {
        res.references.forEach((ref: any) => {
          if (ref.product_spu_customable_image_id == image.customableImage.id && ref.type == 2) {
            dss_image_price = new BigNumber(ref.customizedImage.ref_price).plus(dss_image_price).toFixed(2)
          }
        })
      }
      custom_price.push({
        id: image.customableImage.id,
        name: image.customableImage.name,
        price: image.customableImage.price,
        discount_amount_scale: image.customized_areas.discount_amount_scale,
        customized_areas_scale: image.customized_areas.customized_areas_scale,
        customized_areas_scale_title,
        technology: {
          name: '',
          price: 0
        },
        dss_image_price
      })
    }
  })
  data.custom_price = custom_price
  res.price_detail = data
  customSpu.value = res as CustomSpu
  customSpu.value.customSkus.forEach((customSku) => {
    if (customSku && customSku.sku && customSku.sku.attrs) {
      const color = customSku.sku.attrs.find((a) => {
        return a.attribute.key === "color"
      })
      customSku.color = convertColor(color?.value || color?.object_value)
    }
  })
  const {
    colors: cs,
    sizes: ss,
    customSkus: ks
  } = convertCustomSpuToSimpleSpu(customSpu.value)
  colors.value = cs
  sizes.value = ss
  customSkus.value = ks
  currentCustomSpuData.$patch({ customSpu: customSpu.value })
  createCustomImages()
})

const bigContainer = ref()

// 小图dom数组
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const allImageEditors: any = {}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createEditorRef = computed(() => (value: any) => {
  if (value) {
    const id = value.editorId()
    allImageEditors[id] = value
  }
})

const thumbnailContainer = ref()

// 当前展示的大图
const selectedCustomizedImage = ref<CustomizedImage>()
// 点击事件
const onSelect = (item: CustomizedImage) => {
  selectedCustomizedImage.value = item
  if (selectedCustomizedImage.value.color)
    customImage.value = toUrl(
      findCustomableImage(
        selectedCustomizedImage.value.color,
        selectedCustomizedImage.value
      )
    )!
}

// 批量下载合成图
const download = () => {
  try {
    currentBtn.value = "download"
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const images: any = []
    // const customImages: any = {}
    // let count = 0
    const zip = new JSZip()
    let el: any
    Object.keys(allImageEditors).forEach((key) => {
      el = allImageEditors[key] as FabricElement
      const props = el.$props
      // const suffix = '-' + (props.name || '')
      const suffixWithColor = '-' + (props.name || '') + '-' + (props.colorName || '')
      const data = el.exportFullImageAsBlob()
      images.push({
        name: key + suffixWithColor,
        data
      })
      // if (!customImages[el.$props.customizedImage]) {
      //   const index = count
      //   images.push(fetch(el.$props.customizedImage).then((res) =>
      //     res.blob()).then(blob => blobToBase64(blob)).then(base64 => {
      //       return {
      //         name: `Customized-${index}` + suffix,
      //         data: new Blob([el.convertBase64PNGToBinWithDPI(base64, 300)], { type: 'image/png' })
      //       }
      //     }))
      //   customImages[el.$props.customizedImage] = true
      //   count++
      // }
    })

    Promise.all(images).then((img) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      img.forEach((element: any) => {
        zip.file(`${element.name}-${new Date().getTime()}.png`, element.data, {
          base64: true
        })
      })
      zip.generateAsync({ type: "blob" }).then(function (content) {
        saveAs(content, customSpu.value?.name)
      })
    })
  } catch (error) {
    message.error(t('CustomSpus.CustomSpu.networkError'))
    console.log(error)
  }
}

const router = useRouter()
const onGoBack = () => {
  router.push({ name: 'customSpus', params: { from: 'detail' } })
}

const createCustomImages = () => {
  colors.value.forEach((color) => {
    customSpu.value?.customizedImages.forEach((i) => {
      const customizedImage: CustomizedImage = convertToCustomizedImage(i)
      customizedImage.color = color
      thumbnails.value.push(customizedImage)
    })
  })

  selectedCustomizedImage.value = thumbnails.value[0]
}

const thumbnailWrapper = computed(() => {
  return thumbnailContainer.value
    ? {
      value: thumbnailContainer.value,
      width: thumbnailContainer.value.offsetWidth,
      height: thumbnailContainer.value.offsetHeight
    }
    : undefined
})
</script>

<style scoped lang="less">
.custom-spus {
  width: 100%;
  height: 100%;
  overflow: auto;

  .card {
    height: 100%;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;

    .title {
      display: flex;
      height: 55px;
      padding: 15px 32px;
      border-bottom: 1px solid #e9e9e9;
      align-items: center;

      h4 {
        margin: 0 0 0 10px;
      }
    }

    .content {
      flex: 1;
      width: 100%;
      display: flex;
      padding: 20px;
      box-sizing: border-box;
      gap: 20px;

      .aside {
        width: 40%;
        height: 100%;
        display: flex;
        flex-direction: column;

        .top {
          flex: 1;
          display: flex;
          background-color: #f6f6f6;

          .big {
            width: 100%;
            height: 100%;
            display: flex;
            flex: 1;
            justify-content: center;
            align-items: center;

            @-moz-document url-prefix() {
              :deep(.fabric-div) {
                width: 30vw !important;
                height: 30vw !important;
              }

              :deep(.canvas-container) {
                width: 100% !important;
                height: 100% !important;
              }

              :deep(.lower-canvas) {
                width: 100% !important;
                height: 100% !important;
              }

              :deep(.upper-canvas) {
                width: 100% !important;
                height: 100% !important;
              }
            }
          }
        }

        .thumbnail {
          width: 100%;
          height: 100px;
          display: flex;
          margin-top: 15px;
          margin-bottom: 15px;
          overflow-x: scroll;
          overflow-y: hidden;

          .hidden {
            width: 100px;
            height: 100px;
            position: absolute;
            visibility: hidden;
            display: block;
          }

          .thumbnail-container {
            width: 100px;
            height: 100px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-right: 15px;

            @-moz-document url-prefix() {
              :deep(.fabric-div) {
                width: 100px !important;
                height: 100px !important;
              }

              :deep(.canvas-container) {
                width: 100px !important;
                height: 100px !important;
              }

              :deep(.lower-canvas) {
                width: 100px !important;
                height: 100px !important;
              }

              :deep(.upper-canvas) {
                width: 100px !important;
                height: 100px !important;
              }
            }
          }
        }

        .operation {
          display: flex;
          gap: 10px;

          button {
            height: 40px;
            width: 50%;
          }
        }
      }
    }

    .detail {
      width: 60%;
      display: flex;
    }

    .box {
      padding: 5px 5px;
      margin-right: 15px;
      margin-bottom: 15px;
      border: 1px solid #f5f5f5;
      background-color: #eff1f4;
    }
  }
}
</style>
